import { ConditionViewerItem, ConditionViewerTerm } from '@customerio/pluma-components/react';
import { useQuery } from '@tanstack/react-query';
import {
  getOptOutIdentities,
  normalizeOptOutChannel,
  optOutChannelLabel,
  type OptOutChannel,
} from '@/features/condition-builder';
import { getEnvironmentQueryOptions } from '@/services/db/environment/queries';
import { getDynamicSenderNamesQueryOptions } from '@/services/db/identity/queries';
import { isEmptyString } from '@/utils/string';
import type { OptOutNode } from '../../types';
import { getIconForNode } from '../data-source-icon';

function useSenderLabel(node: OptOutNode, workspaceId: number): string | undefined {
  const sender = node.sender ?? '';
  const channel = normalizeOptOutChannel(node.channel);
  const enabled = workspaceId > 0 && sender !== '';

  const environment = useQuery({ ...getEnvironmentQueryOptions(workspaceId), enabled });
  const name = channelSenderName(sender, channel, environment.data?.identities);
  // The environment omits deleted senders, but conditions can still reference a deleted dynamic sender.
  const dynamicSenderNames = useQuery({
    ...getDynamicSenderNamesQueryOptions(workspaceId),
    enabled: enabled && channel === 'sms' && environment.isSuccess && name == null,
  });
  return senderLabel(sender, name, dynamicSenderNames.data);
}

function channelSenderName(
  sender: string,
  channel: OptOutChannel,
  identities: Parameters<typeof getOptOutIdentities>[0] = [],
): string | undefined {
  const lower = sender.toLowerCase();
  const name = getOptOutIdentities(identities, channel).find(
    (identity) => identity.phone.toLowerCase() === lower,
  )?.name;
  return isEmptyString(name) ? undefined : name;
}

function senderLabel(
  sender: string,
  name: string | undefined,
  dynamicSenderNames: Record<string, string> | undefined,
): string | undefined {
  if (sender === '') return undefined;
  return name ?? dynamicSenderNames?.[sender.toLowerCase()] ?? sender;
}

type OptOutConditionProps = { node: OptOutNode; workspaceId: number };

export function OptOutCondition({ node, workspaceId }: OptOutConditionProps) {
  const sender = useSenderLabel(node, workspaceId);
  const operator = node.inverse ? 'User is not opted out of' : 'User is opted out of';
  const fallback = node.channel == null ? 'communications' : optOutChannelLabel(node.channel);
  return (
    <ConditionViewerItem icon={getIconForNode(node)}>
      {operator} <ConditionViewerTerm>{sender ?? fallback}</ConditionViewerTerm>
    </ConditionViewerItem>
  );
}
